import React, { useState, useCallback } from 'react';
import styles from './styles.module.scss';
import { SlideInput, CapFeedback } from '../../types';
import type { OptionalConsentAnswers } from '../../../shared/consent/OptionalConsents';
import { isValidPhone, isValidEmail, isValidZipCode, hasAllSameDigits } from '../../utils/helpers';
import ZipCodeInput from '../ZipCodeInput';

const PRIVACY_URL = 'https://www.enpal.com/it/protezione-dei-dati';

interface FormInputProps {
  inputs: SlideInput[];
  values: Record<string, string>;
  onChange: (name: string, value: string) => void;
  onSubmit: () => void;
  submitLabel?: string;
  showBackButton?: boolean;
  onBack?: () => void;
  backLabel?: string;
  isPhoneSlide?: boolean;
  isLocationSlide?: boolean;
  capFeedback?: CapFeedback | null;
  isCapValid?: boolean | null;
  optionalConsents?: OptionalConsentAnswers;
  onOptionalConsentsChange?: (answers: OptionalConsentAnswers) => void;
  onPhoneContinue?: (answers: OptionalConsentAnswers) => void;
}

interface PhoneConsentsProps {
  answers: OptionalConsentAnswers;
  phoneValid: boolean;
  onChange: (answers: OptionalConsentAnswers) => void;
  onContinue: (answers: OptionalConsentAnswers) => void;
}

const PhoneConsents: React.FC<PhoneConsentsProps> = ({
  answers,
  phoneValid,
  onChange,
  onContinue,
}) => {
  const partnerChecked = answers.thirdPartyMarketing === true;

  const togglePartner = () => {
    onChange({ ...answers, thirdPartyMarketing: !partnerChecked });
  };

  return (
    <div className={styles.phoneConsents}>
      <p className={styles.consentHint}>Facoltativi, revocabili in ogni momento</p>

      <label className={styles.partnerCard}>
        <span className={styles.partnerCardTop}>
          <span className={styles.partnerTitle}>Comunicazione dei dati ai partner</span>
          <input
            type="checkbox"
            className={`${styles.consentCheckbox} ${
              phoneValid && !partnerChecked ? styles.consentCheckboxReady : ''
            }`}
            checked={partnerChecked}
            onChange={togglePartner}
          />
        </span>
        <span className={styles.partnerDescription}>
          Acconsento a comunicare i miei dati ai partner in{' '}
          <a
            className={styles.privacyLink}
            href={PRIVACY_URL}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(event) => event.stopPropagation()}
          >
            elenco
          </a>
          , per ricevere le loro offerte. Agiscono come titolari autonomi.
        </span>
      </label>

      <button
        type="button"
        className={`${styles.discoverCta} ${phoneValid ? styles.discoverCtaReady : ''}`}
        onClick={() => onContinue(answers)}
      >
        Scopri il tuo risparmio
      </button>

      <p className={styles.callNote}>
        Ti chiameremo per il preventivo; la chiamata può essere registrata.{' '}
        <a
          className={styles.privacyLink}
          href={PRIVACY_URL}
          target="_blank"
          rel="noopener noreferrer"
        >
          Informativa privacy
        </a>
      </p>
    </div>
  );
};

/**
 * Componente form con input fields
 * Include feedback speciale per il CAP
 */
const FormInput: React.FC<FormInputProps> = ({
  inputs,
  values,
  onChange,
  onSubmit,
  submitLabel = 'Avanti',
  showBackButton = true,
  onBack,
  backLabel = 'Indietro',
  isPhoneSlide = false,
  isLocationSlide = false,
  capFeedback,
  isCapValid,
  optionalConsents,
  onOptionalConsentsChange,
  onPhoneContinue,
}) => {
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [touched, setTouched] = useState<Record<string, boolean>>({});

  // Validate a single input
  const validateInput = useCallback((input: SlideInput, value: string): string => {
    if (input.required && !value.trim()) {
      return 'Campo obbligatorio';
    }

    switch (input.name) {
      case 'phone':
        if (value && !isValidPhone(value)) {
          if (hasAllSameDigits(value)) {
            return 'Il numero non può avere tutte le cifre uguali.';
          }
          return 'Numero di telefono non valido';
        }
        break;
      case 'email':
        if (value && !isValidEmail(value)) {
          return 'Indirizzo email non valido';
        }
        break;
      case 'zip':
        if (value && !isValidZipCode(value)) {
          return 'CAP non valido (5 cifre)';
        }
        break;
    }

    // Custom pattern validation
    if (input.pattern && value) {
      const regex = new RegExp(input.pattern);
      if (!regex.test(value)) {
        return 'Formato non valido';
      }
    }

    return '';
  }, []);

  // Handle input change
  const handleChange = (input: SlideInput, value: string) => {
    onChange(input.name, value);

    // Validate on change if already touched
    if (touched[input.name]) {
      const error = validateInput(input, value);
      setErrors((prev) => ({ ...prev, [input.name]: error }));
    }
  };

  // Handle input blur
  const handleBlur = (input: SlideInput) => {
    setTouched((prev) => ({ ...prev, [input.name]: true }));
    const error = validateInput(input, values[input.name] || '');
    setErrors((prev) => ({ ...prev, [input.name]: error }));
  };

  // Check if form is valid
  const isFormValid = useCallback(() => {
    const inputsValid = inputs.every((input) => {
      const value = values[input.name] || '';
      const error = validateInput(input, value);
      return !error;
    });

    // Per la slide CAP, richiedi anche la validazione API
    if (isLocationSlide) {
      return inputsValid && isCapValid === true;
    }

    return inputsValid;
  }, [inputs, values, validateInput, isLocationSlide, isCapValid]);

  // Handle form submit
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    // Validate all inputs
    const newErrors: Record<string, string> = {};
    let hasErrors = false;

    inputs.forEach((input) => {
      const error = validateInput(input, values[input.name] || '');
      if (error) {
        hasErrors = true;
        newErrors[input.name] = error;
      }
    });

    setErrors(newErrors);
    setTouched(
      inputs.reduce((acc, input) => ({ ...acc, [input.name]: true }), {})
    );

    if (isPhoneSlide && optionalConsents && onPhoneContinue) {
      submitPhone(optionalConsents);
      return;
    }

    if (!hasErrors && isFormValid()) {
      onSubmit();
    }
  };

  const submitPhone = (answers: OptionalConsentAnswers) => {
    const newErrors: Record<string, string> = {};
    let hasErrors = false;

    inputs.forEach((input) => {
      const error = validateInput(input, values[input.name] || '');
      if (error) {
        hasErrors = true;
        newErrors[input.name] = error;
      }
    });

    setErrors(newErrors);
    setTouched(inputs.reduce((acc, input) => ({ ...acc, [input.name]: true }), {}));

    if (hasErrors || !isFormValid() || !onPhoneContinue) {
      return;
    }

    const submitted: OptionalConsentAnswers = {
      enpalMarketingConsent: false,
      thirdPartyMarketing: answers.thirdPartyMarketing === true,
      thirdPartyProfiling: false,
    };
    onOptionalConsentsChange?.(submitted);
    onPhoneContinue(submitted);
  };

  const getInputLabel = (input: SlideInput): string | null => {
    if (isPhoneSlide && input.name === 'phone') {
      return 'Numero di telefono *';
    }
    return null;
  };

  // Render CAP feedback message
  const renderCapFeedback = () => {
    if (!isLocationSlide) return null;

    const zipValue = values['zip'] || '';

    // Non mostrare nulla durante la digitazione (0-4 cifre), le caselle sono visibili sotto
    if (zipValue.length < 5) {
      return null;
    }

    if (isCapValid === false) {
      return (
        <div className={`${styles.capFeedbackMessage} ${styles.invalid}`}>
          ❌ CAP non valido
        </div>
      );
    }

    if (isCapValid === true && capFeedback) {
      return (
        <div className={`${styles.capFeedbackMessage} ${styles.valid}`}>
          <span className={styles.bonusCount}>{capFeedback.bonusCount} bonus</span> disponibili per il CAP <strong>{zipValue}</strong>
        </div>
      );
    }

    return null;
  };

  return (
    <form className={styles.formGroup} onSubmit={handleSubmit}>
      {/* CAP Feedback Container */}
      {isLocationSlide && (
        <div className={styles.capFeedbackContainer}>
          {renderCapFeedback()}
        </div>
      )}

      {inputs.map((input) => {
          // Determina il valore autocomplete corretto per ogni campo
          const getAutocomplete = (name: string): string => {
            switch (name) {
              case 'name': return 'name';
              case 'email': return 'email';
              case 'phone': return 'tel';
              case 'zip': return 'postal-code';
              default: return 'off';
            }
          };

          // Usa ZipCodeInput per il campo CAP nella slide location
          if (isLocationSlide && input.name === 'zip') {
            const zipValue = values['zip'] || '';
            const showValidatedZip = isCapValid === true && zipValue.length === 5;

            return (
              <div key={input.name} className={styles.inputWrapper}>
                {!showValidatedZip ? (
                  <>
                    <ZipCodeInput
                      value={zipValue}
                      onChange={(value) => handleChange(input, value)}
                      isValid={isCapValid ?? null}
                      isChecking={false}
                    />
                    {touched[input.name] && errors[input.name] && (
                      <p className={styles.errorMessage}>{errors[input.name]}</p>
                    )}
                  </>
                ) : (
                  <div className={styles.validatedZipDisplay}>
                    <span className={styles.checkmark}>✓</span>
                    <span className={styles.zipNumber}>{zipValue}</span>
                  </div>
                )}
              </div>
            );
          }

          const label = getInputLabel(input);

          // Rendering standard per altri input
          return (
            <div key={input.name} className={styles.inputWrapper}>
              {label && <label className={styles.inputLabel}>{label}</label>}
              <div className={isLocationSlide && input.name === 'zip' ? styles.inputWithCheck : ''}>
                <input
                  className={`${styles.input} ${
                    touched[input.name] && errors[input.name] ? styles.inputError : ''
                  } ${isPhoneSlide && input.name === 'phone' ? styles.phoneInput : ''}`}
                  type={input.type}
                  name={input.name}
                  placeholder={input.placeholder}
                  value={values[input.name] || ''}
                  onChange={(e) => handleChange(input, e.target.value)}
                  onBlur={() => handleBlur(input)}
                  required={input.required}
                  maxLength={input.maxLength}
                  autoComplete={getAutocomplete(input.name)}
                />
              </div>
              {touched[input.name] && errors[input.name] && (
                <p className={styles.errorMessage}>{errors[input.name]}</p>
              )}
            </div>
          );
      })}

      {!isPhoneSlide && (
      <div className={styles.buttonGroup}>
        {/* Mostra "Modifica CAP" nella slide location quando il CAP è validato */}
        {isLocationSlide && isCapValid === true && values['zip']?.length === 5 && (
          <button
            type="button"
            className={styles.btnModifyCap}
            onClick={() => onChange('zip', '')}
          >
            Modifica CAP
          </button>
        )}
        {/* Mostra "Indietro" nelle altre slide */}
        {showBackButton && onBack && !isLocationSlide && (
          <button type="button" className={styles.btnPrev} onClick={onBack}>
            {backLabel}
          </button>
        )}
        <button
          type="submit"
          className={styles.btnNext}
          disabled={!isFormValid()}
        >
          {submitLabel}
        </button>
      </div>
      )}

      {isPhoneSlide && optionalConsents && onOptionalConsentsChange && onPhoneContinue && (
        <PhoneConsents
          answers={optionalConsents}
          phoneValid={isFormValid()}
          onChange={onOptionalConsentsChange}
          onContinue={submitPhone}
        />
      )}
    </form>
  );
};

export default FormInput;
